import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import { useAppContextNonNull } from "@contexts/AppContext";
import ScreenOptionsEnum from "@models/ScreenOptionsEnum";
import NotificationLevelEnum from "@models/NotificationLevelEnum";
import FeedbackService, { FEEDBACK_SOURCE_FILTER_BAR } from "@services/FeedbackService";
import { toQueryString, toQueryStringParts } from "@utils/apiQueryString";
import { useFilterPresets } from "@hooks/shared/useFilterPresets";
import { buildReportsFilterSections, REPORTS_DEFAULT_FILTERS } from "@config/filters/reportsFilters";
import FilterBar from "../../../shared/FilterBar/FilterBar";
import { useUrlFilters } from "../../../shared/FilterBar/useUrlFilters";

export const REPORTS_FILTER_ACTIONS_ID = "reports-filter-actions";

/** The Reports list URL carries only filters. */
const NON_FILTER_PARAMS: string[] = [];

type Props = {
    /** The supplier has custom reports, so the `custom` report type is offered */
    hasCustomReports: boolean;
    isSearching: boolean;
    /** Existing Reports fetch: receives the complete `?a=b&c=d` query string ("" for no filters) */
    searchFn: (queryString: string) => void;
};

/**
 * Reports (ready reports list) integration of the shared FilterBar (v2 search UI).
 * Screen key is ScreenOptionsEnum.REPORTS ("reports"), which is also the presets JSON key.
 */
export const ReportsFilterBar = (props: Props) => {
    const { hasCustomReports, searchFn } = props;
    const { userModel, supplierModel, vendorModel, uiContext } = useAppContextNonNull();
    const intl = useIntl();
    const navigate = useNavigate();

    const formatMessage = useCallback(
        (id: string) => (intl.messages && intl.messages[id] !== undefined ? intl.formatMessage({ id }) : id),
        [intl]
    );

    const sections = useMemo(
        () => buildReportsFilterSections({ userModel, supplierModel, hasCustomReports, formatMessage }),
        [userModel, supplierModel, hasCustomReports, formatMessage]
    );

    // Deep links (e.g. /reports?report_type=commission_report): read on mount and again on Back / Forward.
    const { initialFilters, historyKey, isFirstSearch } = useUrlFilters(sections, NON_FILTER_PARAMS);

    const { presets, onPresetsChange } = useFilterPresets(ScreenOptionsEnum.REPORTS);

    const handleSearch = useCallback(
        (query: { [apiKey: string]: unknown }) => {
            const search = toQueryString(toQueryStringParts(query));

            // Keep the URL in sync so filtered views can be shared / deep-linked.
            // The mount search replaces (no extra history entry); user searches push.
            navigate({ pathname: "/reports", search }, { replace: isFirstSearch.current });
            isFirstSearch.current = false;

            searchFn(search);
        },
        [navigate, searchFn, isFirstSearch]
    );

    const handleFeedback = useCallback(
        async (message: string) => {
            try {
                await FeedbackService.sendFeedback(message, FEEDBACK_SOURCE_FILTER_BAR, { userModel, supplierModel, vendorModel });
                uiContext.notifyFn(null, { message: "feedback_sent", level: NotificationLevelEnum.success });
            } catch (error) {
                console.error("Failed to send FilterBar feedback", error);
                uiContext.notifyFn(null, { message: "feedback_failed", level: NotificationLevelEnum.error });
                throw error;
            }
        },
        [userModel, supplierModel, vendorModel, uiContext]
    );

    if (presets === null) {
        return null;
    }

    return (
        <FilterBar
            key={historyKey}
            sections={sections}
            presets={presets}
            onSearch={handleSearch}
            onPresetsChange={onPresetsChange}
            screenKey={ScreenOptionsEnum.REPORTS}
            mode={"drawer"}
            defaultFilters={REPORTS_DEFAULT_FILTERS}
            initialFilters={initialFilters}
            allLabel={"all_reports"}
            actionsContainerId={REPORTS_FILTER_ACTIONS_ID}
            isSearching={props.isSearching}
            onSendFeedback={handleFeedback}
        />
    );
};

export default ReportsFilterBar;
